import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { Tabs } from "antd";
import { Save } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HRMSNavigationRoutes from "../../utils/navigationRoutes";

const EmployeeAddFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [savingAll, setSavingAll] = useState(false);

  const handleSaveAndRedirect = async (formRef: any) => {
    try {
      await formRef.current?.handleSave();
      navigate("/hrms" + HRMSNavigationRoutes.EMP_LIST);
    } catch (error) {
      notify.error("Failed to save employee");
      console.error("Save error:", error);
    }
  };

  // New: Save all tabs sequentially (best-effort). Creates main record first and
  // propagates created id to other forms before saving them.
  const handleSaveAllTabs = async () => {
    setSavingAll(true);
    try {
      // Save details first
      const detailsRes: any = await formDetailsRef.current?.handleSave();

      // Extract created/updated id. API may return { id } or full object.
      const createdId = detailsRes?.id ?? detailsRes?.data?.id ?? id;

      // If we have a created id, set it into other forms so they save correctly
      const setIfNeeded = (form: any) => {
        try {
          if (createdId) {
            form.setInitialValues({ employee_id: createdId });
          }
        } catch (_e) {
          // ignore
        }
      };

      setIfNeeded(formJoining);
      setIfNeeded(formDocuments);
      setIfNeeded(formAssetIssued);
      setIfNeeded(formEmployment);
      setIfNeeded(formAttendanceLeaves);
      setIfNeeded(formSalary);
      setIfNeeded(formPersonalDetails);
      setIfNeeded(formProfile);
      setIfNeeded(formExit);

      // Save other tabs (best-effort)
      const saveRefs = [
        formJoiningRef,
        formDocumentsRef,
        formAssetIssuedRef,
        formAddressContactRef,
        formAttendanceLeavesRef,
        formSalaryRef,
        formPersonalDetailsRef,
        formProfileRef,
        formExitRef,
      ];

      const results: Array<{ ref: any; ok: boolean; err?: any }> = [];
      for (const r of saveRefs) {
        try {
          await r.current?.handleSave();
          results.push({ ref: r, ok: true });
        } catch (e) {
          results.push({ ref: r, ok: false, err: e });
        }
      }

      const failed = results.filter(x => !x.ok);
      if (failed.length === 0) {
        notify.success("All tabs saved successfully");
        navigate("/hrms" + HRMSNavigationRoutes.EMP_LIST);
      } else {
        notify.warning(`${failed.length} tab(s) failed to save. Please check.`);
      }
    } catch (error) {
      notify.error("Failed to save employee details");
      console.error(error);
    } finally {
      setSavingAll(false);
    }
  };

  const schemaDetails = "Hrms Overview";
  const schemaJoining = "Hrms Joining";
  const schemaDocuments = "Hrms Document";
  const schemaAssetIssued = "Hrms Asset Issued";
  const schemaAddressContact = "Hrms Address Contact";
  const schemaAttendanceLeaves = "Hrms Attendance And Leaves";
  const schemaSalary = "Hrms Salary";
  const schemaPersonalDetails = "Hrms Personal Details";
  const schemaProfile = "Hrms Profile";
  const schemaExit = "Hrms Employee Exit";

  const [isEditingDoc] = useState(false);
  const [editingDocRecord] = useState<any>(null);

  const formDetails = useMemo(() => createForm({ validateFirst: true }), []);
  const formJoining = useMemo(() => createForm({ validateFirst: true }), []);
  const formDocuments = useMemo(() => createForm({ validateFirst: true }), []);
  const formAssetIssued = useMemo(
    () => createForm({ validateFirst: true }),
    []
  );
  const formEmployment = useMemo(() => createForm({ validateFirst: true }), []);
  const formAttendanceLeaves = useMemo(
    () => createForm({ validateFirst: true }),
    []
  );
  const formSalary = useMemo(() => createForm({ validateFirst: true }), []);
  const formPersonalDetails = useMemo(
    () => createForm({ validateFirst: true }),
    []
  );

  const formProfile = useMemo(() => createForm({ validateFirst: true }), []);
  const formExit = useMemo(() => createForm({ validateFirst: true }), []);

  const formDetailsRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formJoiningRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formDocumentsRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formAssetIssuedRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formAddressContactRef = useRef<{ handleSave: () => Promise<any> }>(
    null
  );
  const formAttendanceLeavesRef = useRef<{ handleSave: () => Promise<any> }>(
    null
  );
  const formSalaryRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formPersonalDetailsRef = useRef<{ handleSave: () => Promise<any> }>(
    null
  );
  const formProfileRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formExitRef = useRef<{ handleSave: () => Promise<any> }>(null);

  if (isEditMode) {
    formDetails.setInitialValues({ employee_id: id });
    formJoining.setInitialValues({ employee_id: id });
    formDocuments.setInitialValues({ employee_id: id });
    formAssetIssued.setInitialValues({ employee_id: id });
    formEmployment.setInitialValues({ employee_id: id });
    formAttendanceLeaves.setInitialValues({ employee_id: id });
    formSalary.setInitialValues({ employee_id: id });
    formPersonalDetails.setInitialValues({ employee_id: id });
    formProfile.setInitialValues({ employee_id: id });
    formExit.setInitialValues({ employee_id: id });
  }

  return (
    <>
      <PageHeader
        className="custom-page-header"
        title={isEditMode ? "Edit Employee" : "Create Employee"}
        onBack={() => navigate("/hrms" + HRMSNavigationRoutes.EMP_LIST)}
        extra={[
          <CustomButton
            key="save"
            type="primary"
            icon={<Save size={16} />}
            onClick={handleSaveAllTabs}
            loading={savingAll}
          >
            {isEditMode ? "Update" : "Save"}
          </CustomButton>,
        ]}
      />
      <Tabs
        style={{ marginTop: 24 }}
        items={[
          {
            key: "1",
            label: "Overview",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formDetails}
                  schemaName={schemaDetails}
                  recordId={id}
                  ref={formDetailsRef}
                />
                <CustomButton
                  key="save"
                  type="primary"
                  icon={<Save size={16} />}
                  onClick={() => handleSaveAndRedirect(formDetailsRef)}
                >
                  Save
                </CustomButton>
              </>
            ),
          },
          {
            key: "2",
            label: "Joining",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formJoining}
                  schemaName={schemaJoining}
                  recordId={id}
                  ref={formJoiningRef}
                />
                <CustomButton
                  key="save"
                  type="primary"
                  icon={<Save size={16} />}
                  onClick={() => handleSaveAndRedirect(formDetailsRef)}
                >
                  Save
                </CustomButton>
                {/* per-tab save removed — use header Save to save all tabs */}
              </>
            ),
          },
          {
            key: "3",
            label: "Document",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formDocuments}
                    schemaName={schemaDocuments}
                    recordId={isEditingDoc ? editingDocRecord?.id : ""}
                    ref={formDocumentsRef}
                  />
                </div>
              </div>
            ),
          },
          {
            key: "4",
            label: "Asset Issued",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formAssetIssued}
                    schemaName={schemaAssetIssued}
                    recordId={id}
                    ref={formAssetIssuedRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "5",
            label: "Address & Contact",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formEmployment}
                    schemaName={schemaAddressContact}
                    recordId={id}
                    ref={formAddressContactRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "6",
            label: "Attendance & Leaves",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formAttendanceLeaves}
                    schemaName={schemaAttendanceLeaves}
                    recordId={id}
                    ref={formAttendanceLeavesRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "7",
            label: "Salary",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formSalary}
                    schemaName={schemaSalary}
                    recordId={id}
                    ref={formSalaryRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "8",
            label: "Personal Details",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formPersonalDetails}
                    schemaName={schemaPersonalDetails}
                    recordId={id}
                    ref={formPersonalDetailsRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "9",
            label: "Profile",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formProfile}
                    schemaName={schemaProfile}
                    recordId={id}
                    ref={formProfileRef}
                  />
                  {/* per-tab save removed — use header Save to save all tabs */}
                </div>
              </div>
            ),
          },
          {
            key: "10",
            label: "Employee Exit",
            children: (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <SchemaFormAutoSubmit
                    form={formExit}
                    schemaName={schemaExit}
                    recordId={id}
                    ref={formExitRef}
                  />
                  {isEditMode && (
                    <div className="flex justify-end">
                      <CustomButton
                        type="primary"
                        onClick={async () => {
                          try {
                            await formExitRef.current?.handleSave();
                            notify.success(
                              "Exit information updated successfully"
                            );
                          } catch {
                            notify.error("Failed to update exit information");
                          }
                        }}
                      >
                        Update Exit Information
                      </CustomButton>
                    </div>
                  )}
                </div>
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

export default EmployeeAddFormPage;
